import { CartLinesUpdateEvent } from '@shopify/events';

/* Sania Maskatiya — homepage behaviour (slideshow, product tabs, wishlist toggle). */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SmSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.sm-slide')];
    this.dots = [...this.querySelectorAll('.sm-slider button')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.delay = Number(this.dataset.autoplay || 0) * 1000;

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.show(i);
        this.start();
      })
    );

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target));
      if (i > -1) this.show(i);
      this.stop();
      this.paused = true;
    });
    this.addEventListener('shopify:block:deselect', () => {
      this.paused = false;
      this.start();
    });

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    // Hidden slides' media stays display:none (unfetched) until the page has loaded.
    this.onLoad = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.arm(), { timeout: 2000 }) : setTimeout(() => this.arm(), 1200));
    if (document.readyState === 'complete') this.onLoad();
    else window.addEventListener('load', this.onLoad, { once: true });

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('load', this.onLoad);
  }

  /** Lets the other slides' images load. */
  arm() {
    if (this.classList.contains('is-ready')) return;
    this.classList.add('is-ready');
    // Fetch them now so a fade never reveals an empty slide.
    for (const img of this.querySelectorAll('.sm-slide-frame img[loading="lazy"]')) img.loading = 'eager';
  }

  show(index) {
    this.arm();
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
  }

  start() {
    this.stop();
    if (this.paused || !this.delay || this.slides.length < 2 || reducedMotion.matches) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
  }

  stop() {
    window.clearInterval(this.timer);
  }
}

class SmTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.viewAll = this.querySelector('[data-sm-view-all]');

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const next = (i + step + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });

    this.addEventListener('shopify:block:select', (event) => {
      const i = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
      if (i > -1) this.select(i);
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    });

    const url = this.tabs[index]?.dataset.url;
    if (this.viewAll && url) this.viewAll.href = url;
  }
}

/* Wishlist: stores product handles locally and emits `sm:wishlist:change`
   so a wishlist app can be wired in later without touching markup. */
const WISHLIST_KEY = 'sm:wishlist';

function readWishlist() {
  try {
    return new Set(JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function syncWishlistButtons(root = document) {
  const saved = readWishlist();
  root.querySelectorAll('.sm-wishlist[data-handle]').forEach((button) => {
    button.setAttribute('aria-pressed', String(saved.has(button.dataset.handle)));
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('.sm-wishlist[data-handle]');
  if (!button) return;

  const saved = readWishlist();
  const handle = button.dataset.handle;
  const added = !saved.has(handle);
  added ? saved.add(handle) : saved.delete(handle);

  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify([...saved]));
  } catch {}

  syncWishlistButtons();
  document.dispatchEvent(new CustomEvent('sm:wishlist:change', { detail: { handle, added, items: [...saved] } }));
});

document.addEventListener('shopify:section:load', (event) => syncWishlistButtons(event.target));
syncWishlistButtons();

/* Top bar: cross-fades between messages when there is more than one. */
class SmRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.sm-topbar-message')];
    this.index = 0;
    if (this.items.length < 2 || reducedMotion.matches) return;

    const delay = Number(this.dataset.interval || 5) * 1000;
    this.timer = window.setInterval(() => this.show(this.index + 1), delay);
    this.addEventListener('shopify:block:select', (event) => {
      window.clearInterval(this.timer);
      const i = this.items.indexOf(event.target);
      if (i > -1) this.show(i);
    });
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  show(index) {
    this.index = index % this.items.length;
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === this.index);
      item.setAttribute('aria-hidden', String(i !== this.index));
    });
  }
}

/* Header: the hamburger hides/shows the nav row. Scrolling down shrinks the
   header and hides the row once; the hamburger can bring it back. */
class SmHeader extends HTMLElement {
  static CONDENSE_AT = 150;
  static EXPAND_AT = 40; // hysteresis so the shrinking header can't flicker the state

  connectedCallback() {
    this.toggle = this.querySelector('[data-sm-menu-toggle]');
    this.navWrap = this.querySelector('.sm-navigation-wrap');
    this.navHidden = false;

    // Phones: the hamburger opens the side menu. Desktop: it shows/hides the nav row.
    this.drawer = this.querySelector('[data-sm-drawer]');
    this.mobileQuery = window.matchMedia('(max-width: 700px)');
    this.toggle?.addEventListener('click', () => {
      if (this.drawer && this.mobileQuery.matches) this.openDrawer();
      else this.setNavHidden(!this.navHidden);
    });
    this.#setupDrawer();
    this.navWrap?.addEventListener('transitionend', (event) => {
      if (event.target !== this.navWrap || this.navHidden) return;
      this.classList.add('is-nav-settled');
      this.#reserveHeight();
    });

    if (this.hasAttribute('data-condense')) {
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          const y = Math.max(window.scrollY, this.scroller?.scrollTop || 0);
          if (!this.condensed && y > SmHeader.CONDENSE_AT) this.setCondensed(true);
          else if (this.condensed && y < SmHeader.EXPAND_AT) this.setCondensed(false);
        });
      };
      // Horizon scrolls .page-wrapper on desktop (≥990px) and the window on mobile.
      this.scroller = document.querySelector('.page-wrapper');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller?.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    // Keep Horizon's --header-height in sync with the real (changing) height, and reserve
    // the full (expanded) height on the section so condensing never shifts the page.
    if (this.hasAttribute('data-sticky')) {
      this.section = this.closest('.shopify-section');
      this.resizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
        document.body.style.setProperty('--header-group-height', `${this.offsetHeight}px`);
        this.#reserveHeight();
      });
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  /** Records the expanded height once the header is fully open (not mid-transition). */
  #reserveHeight() {
    if (!this.section || this.condensed || !this.classList.contains('is-nav-settled')) return;
    this.section.style.setProperty('--sm-header-full', `${this.offsetHeight}px`);
  }

  #setupDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    drawer.querySelector('[data-sm-drawer-close]')?.addEventListener('click', () => this.closeDrawer());
    // A click on the backdrop lands on the <dialog> itself.
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) this.closeDrawer();
    });
    // Esc: animate out instead of the instant native close.
    drawer.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDrawer();
    });
    drawer.addEventListener('close', () => {
      drawer.classList.remove('is-closing');
      this.toggle?.setAttribute('aria-expanded', 'false');
    });
    drawer.querySelector('[data-sm-drawer-search]')?.addEventListener('click', () => {
      this.closeDrawer(true);
      /** @type {HTMLElement & { showDialog?: () => void } | null} */
      const search = document.querySelector('#search-modal');
      search?.showDialog?.();
    });
  }

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    this.drawer.showModal();
    this.toggle?.setAttribute('aria-expanded', 'true');
  }

  /** @param {boolean} [instant] */
  closeDrawer(instant = false) {
    const drawer = this.drawer;
    if (!drawer?.open || drawer.classList.contains('is-closing')) return;
    if (instant || reducedMotion.matches) {
      drawer.close();
      return;
    }
    drawer.classList.add('is-closing');
    drawer.querySelector('.sm-menu-drawer__panel')?.addEventListener('animationend', () => drawer.close(), { once: true });
  }

  setCondensed(value) {
    this.condensed = value;
    this.classList.toggle('is-condensed', value);
    this.setNavHidden(value);
  }

  setNavHidden(value) {
    this.navHidden = value;
    this.classList.toggle('is-nav-hidden', value);
    if (value) this.classList.remove('is-nav-settled');
    else if (reducedMotion.matches) this.classList.add('is-nav-settled'); // no transitionend without motion
    this.toggle?.setAttribute('aria-expanded', String(!value));
  }
}

/* Product slider: arrows page the track by one view; a dot/line indicator
   underneath shows the current page. Arrows and dots only appear when there
   is more to scroll. Works for the desktop slider and the mobile carousel. */
class SmCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sm-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-sm-prev]');
    this.next = this.querySelector('[data-sm-next]');
    this.dots = this.querySelector('[data-sm-dots]');

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));
    this.dots?.addEventListener('click', (event) => {
      const dot = event.target.closest('button[data-page]');
      if (dot) this.goTo(Number(dot.dataset.page));
    });

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    // Also fires when a hidden tab panel becomes visible or the viewport changes.
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get behavior() {
    return reducedMotion.matches ? 'auto' : 'smooth';
  }

  page(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: this.behavior });
  }

  goTo(index) {
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: this.behavior });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (!clientWidth) return; // hidden tab panel
    const overflow = scrollWidth > clientWidth + 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    this.toggleAttribute('data-overflow', overflow);

    if (this.prev) this.prev.disabled = !overflow || scrollLeft <= 2;
    if (this.next) this.next.disabled = !overflow || atEnd;

    if (!this.dots) return;
    const pages = overflow ? Math.ceil((scrollWidth - 2) / clientWidth) : 1;
    const current = atEnd ? pages - 1 : Math.min(pages - 1, Math.round(scrollLeft / clientWidth));

    this.dots.hidden = pages < 2;
    this.toggleAttribute('data-paged', pages > 1);

    if (this.dots.childElementCount !== pages) {
      this.dots.replaceChildren(
        ...Array.from({ length: pages }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.page = String(i);
          dot.setAttribute('aria-label', `Go to page ${i + 1} of ${pages}`);
          return dot;
        })
      );
    }
    [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
  }
}

if (!customElements.get('sm-carousel')) customElements.define('sm-carousel', SmCarousel);
/* Quick add: posts to /cart/add.js and announces it with the same standard
   CartLinesUpdateEvent Horizon's product form uses, so the cart drawer opens
   and the cart count/items refresh exactly as on the product page. */
async function smRefreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.json();
}

async function smQuickAdd(button) {
  const bar = button.closest('[data-sm-quick]');
  const variantId = button.dataset.variantId;
  if (!bar || !variantId || bar.classList.contains('is-busy')) return;

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  // Send the request first so the drawer opening (event listeners) can't delay it.
  const request = fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  bar.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  bar.classList.add('is-busy');
  smFlyToCart(bar); // only when Theme settings → Animations → Add to cart animation is on
  const detailBase = { source: 'sm-quick-add', itemCount: 1, productId: bar.dataset.productId };

  try {
    const response = await request.then((r) => r.json());

    const cart = await smRefreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detailBase, items: cart.items, sections: response.sections, didError: Boolean(response.status) },
    });

    smFlash(bar, response.status ? response.description || response.message : button.dataset.labelAdded);
  } catch (error) {
    deferred.reject(error);
    smFlash(bar, 'Please try again');
  } finally {
    bar.classList.remove('is-busy');
  }
}

/* Reuses Horizon's <fly-to-cart> element (assets/fly-to-cart.js, loaded globally)
   to glide the card photo into the bag icon, matching the product page. */
function smFlyToCart(bar) {
  if (bar.dataset.animate !== 'true') return; // Theme settings → Animations → Add to cart animation
  if (!customElements.get('fly-to-cart') || reducedMotion.matches) return;
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const image = bar.closest('.sm-product')?.querySelector('.sm-product-image img:not(.sm-product-alt)');
  const src = image?.currentSrc || image?.src;
  if (!cartIcon || !src) return;

  const fly = /** @type {HTMLElement & { source?: Element, destination?: Element }} */ (
    document.createElement('fly-to-cart')
  );
  fly.classList.add('fly-to-cart--quick');
  fly.style.setProperty('background-image', `url(${src})`);
  fly.style.setProperty('--start-opacity', '0');
  fly.source = bar;
  fly.destination = cartIcon;
  document.body.appendChild(fly);
}

/* Briefly replaces the bar content with a status line, then restores it. */
function smFlash(bar, message) {
  if (!message) return;
  const original = [...bar.childNodes];
  const status = document.createElement('span');
  status.className = 'sm-quick-status';
  status.setAttribute('role', 'status');
  status.textContent = message;
  bar.replaceChildren(status);
  bar.classList.add('is-done');
  window.setTimeout(() => {
    bar.replaceChildren(...original);
    bar.classList.remove('is-done');
  }, 1800);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-sm-quick] button[data-variant-id]');
  if (!button) return;
  event.preventDefault();
  smQuickAdd(button);
});

/* Seasonal favourites: hovering (or focusing) a list link cross-fades the
   large tile to that link's image and points the tile at its URL. */
class SmSeasonal extends HTMLElement {
  connectedCallback() {
    this.feature = this.querySelector('[data-sm-feature]');
    this.layers = [...this.querySelectorAll('.sm-seasonal-layer')];
    this.links = [...this.querySelectorAll('.sm-seasonal-list a[data-layer]')];
    if (!this.feature || this.layers.length < 2) return;

    this.links.forEach((link) => {
      const show = () => this.show(link.dataset.layer, link);
      link.addEventListener('mouseenter', show);
      link.addEventListener('focus', show);
    });

    if (this.hasAttribute('data-reset')) {
      this.querySelector('.sm-seasonal-list')?.addEventListener('mouseleave', () => this.show('0'));
    }

    this.addEventListener('shopify:block:select', (event) => {
      const link = this.links.find((l) => l === event.target || l.contains(event.target));
      if (link) this.show(link.dataset.layer, link);
    });
  }

  show(index, activeLink = null) {
    const layer = this.layers.find((l) => l.dataset.layer === String(index));
    if (!layer) return; // link without an image: keep the current picture
    this.layers.forEach((l) => l.classList.toggle('is-active', l === layer));
    this.links.forEach((l) => l.classList.toggle('is-active', l === activeLink));
    this.feature.href = layer.dataset.url || this.feature.href;
  }
}

if (!customElements.get('sm-seasonal')) customElements.define('sm-seasonal', SmSeasonal);
/* Video reel: plays each muted loop only while it is on screen. */
class SmReel extends HTMLElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];
    if (!this.videos.length) return;

    this.videos.forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.removeAttribute('controls');
    });
    // Posters are deferred in Liquid (data-poster); fetch them shortly before the reel scrolls in.
    this.posterObserver = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          if (!isIntersecting) continue;
          if (target.dataset.poster) target.poster = target.dataset.poster;
          this.posterObserver.unobserve(target);
        }
      },
      { rootMargin: '600px 600px' }
    );
    this.videos.forEach((video) => this.posterObserver.observe(video));

    if (reducedMotion.matches) return; // keep posters only

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) target.play().catch(() => {});
          else target.pause();
        });
      },
      { rootMargin: '0px 120px', threshold: 0.35 }
    );
    this.videos.forEach((video) => this.observer.observe(video));
  }

  disconnectedCallback() {
    this.posterObserver?.disconnect();
    this.observer?.disconnect();
  }
}

if (!customElements.get('sm-reel')) customElements.define('sm-reel', SmReel);
/* Product recommendations: fetch the section from Shopify's recommendations
   endpoint once it nears the viewport; stay hidden if nothing comes back. */
class SmRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-pending') || !this.dataset.url) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    try {
      const html = await fetch(this.dataset.url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('sm-recommendations');
      if (fresh && fresh.innerHTML.trim()) {
        this.innerHTML = fresh.innerHTML;
        this.removeAttribute('data-pending');
        syncWishlistButtons(this);
      } else {
        this.closest('.shopify-section')?.remove();
      }
    } catch {
      this.closest('.shopify-section')?.remove();
    }
  }
}

if (!customElements.get('sm-recommendations')) customElements.define('sm-recommendations', SmRecommendations);
if (!customElements.get('sm-header')) customElements.define('sm-header', SmHeader);
if (!customElements.get('sm-rotator')) customElements.define('sm-rotator', SmRotator);
if (!customElements.get('sm-slideshow')) customElements.define('sm-slideshow', SmSlideshow);
if (!customElements.get('sm-tabs')) customElements.define('sm-tabs', SmTabs);
